import { useState } from "react";
import { Restaurant } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { RestaurantCardHeader } from "./RestaurantCardHeader";
import { RestaurantCardInline } from "./RestaurantCardInline";
import { RestaurantCardMenu } from "./RestaurantCardMenu";
import { RestaurantCardDescription } from "./RestaurantCardDescription";
import { MenuWithItems } from "@/hooks/useAllDailyMenus";

interface RestaurantCardProps {
  restaurant: Restaurant;
  isFavorite: boolean;
  onToggleFavorite: (restaurantId: string) => void;
  menusWithItems?: MenuWithItems[];
  menusLoading?: boolean;
  vouchers?: string[];
  menuDate?: Date;
}

export function RestaurantCard({
  restaurant,
  isFavorite,
  onToggleFavorite,
  menusWithItems,
  menusLoading = false,
  vouchers,
  menuDate,
}: RestaurantCardProps) {
  const [isOpen, setIsOpen] = useState(true);

  // Check if any menu item has allergen description
  const hasAllergens = menusWithItems?.some(menu => 
    menu.items?.some(item => item.description && item.description.trim().length > 0)
  ) ?? false;

  return (
    <Card
      id={restaurant.id}
      className="overflow-hidden scroll-mt-24 transition-all flex flex-col h-full"
    >
      <RestaurantCardHeader
        restaurant={restaurant}
        isFavorite={isFavorite}
        isOpen={isOpen}
        onToggleFavorite={onToggleFavorite}
        onToggleOpen={() => setIsOpen(!isOpen)}
      />
      
      {isOpen && (
        <div className="flex flex-col flex-1">
          <RestaurantCardInline restaurant={restaurant} />
          <RestaurantCardMenu 
            menusWithItems={menusWithItems} 
            isLoading={menusLoading}
            restaurantId={restaurant.id}
            restaurantName={restaurant.name}
            hasOnlineOrdering={(restaurant as any).has_online_ordering}
            menuStartTime={restaurant.menu_start_time}
            menuEndTime={restaurant.menu_end_time}
            onlineOrderingEndTime={(restaurant as any).online_ordering_end_time}
            menuDate={menuDate}
            orderingLogicType={(restaurant as any).ordering_logic_type}
            soupOnlyPrice={(restaurant as any).soup_only_price}
            mainCourseOnlyPrice={(restaurant as any).main_course_only_price}
            dineInAvailable={(restaurant as any).dine_in_available !== false}
            deliveryAvailable={restaurant.delivery_available}
            takeawayAvailable={restaurant.takeaway_available}
            deliveryFee={restaurant.delivery_fee}
            boxPrice={restaurant.box_price}
            minOrderAmount={(restaurant as any).min_order_amount}
            passCommissionToCustomer={(restaurant as any).pass_commission_to_customer}
            commissionPercent={(restaurant as any).commission_percent}
            commissionPerOrder={(restaurant as any).commission_per_order}
            commissionVatApplicable={(restaurant as any).commission_vat_applicable}
            commissionVatRate={(restaurant as any).commission_vat_rate}
            restaurantDeliveryPrice={restaurant.delivery_price}
            restaurantTakeawayPrice={restaurant.takeaway_price}
          />
          <RestaurantCardDescription 
            description={restaurant.description} 
            isPro={(restaurant as any).is_pro}
            vouchers={vouchers}
            hasAllergens={hasAllergens}
          />
        </div>
      )}
    </Card>
  );
}
